import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import MobileNav from '@/components/MobileNav';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import Dashboard from '@/pages/Dashboard';
import WeatherLookup from '@/pages/WeatherLookup';
import SearchHistory from '@/pages/SearchHistory';
import CompareWeather from '@/pages/CompareWeather';
import BulkLookup from '@/pages/BulkLookup';
import MyReports from '@/pages/MyReports';
import ApiDashboard from '@/pages/ApiDashboard';
import ApiDocs from '@/pages/ApiDocs';
import AdminDashboard from '@/pages/AdminDashboard';
import TermsOfService from '@/pages/TermsOfService';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import ReportVerification from '@/pages/ReportVerification';
import SharedReportView from '@/pages/SharedReportView';
import PaymentSuccess from '@/pages/PaymentSuccess';
import PaymentCancel from '@/pages/PaymentCancel';
import '@/App.css';

const PUBLIC_ROUTES = ['/', '/login', '/register', '/terms', '/privacy', '/verify-report', '/success', '/cancel'];

const AppContent = () => {
  const location = useLocation();
  const { user } = useAuth();
  const showMobileNav = user && !PUBLIC_ROUTES.includes(location.pathname) && !location.pathname.startsWith('/shared/');

  useEffect(() => {
    const hideBadge = () => {
      document.querySelectorAll('#emergent-badge, [id*="emergent-badge"]').forEach(el => {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('height', '0', 'important');
        el.style.setProperty('width', '0', 'important');
        el.style.setProperty('overflow', 'hidden', 'important');
      });
    };
    hideBadge();
    const interval = setInterval(hideBadge, 300);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/verify-report" element={<ReportVerification />} />
          <Route path="/shared/:token" element={<SharedReportView />} />
          <Route path="/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          <Route path="/cancel" element={<PaymentCancel />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lookup"
            element={
              <ProtectedRoute>
                <WeatherLookup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <SearchHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compare"
            element={
              <ProtectedRoute>
                <CompareWeather />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bulk"
            element={
              <ProtectedRoute>
                <BulkLookup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <MyReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/developer"
            element={
              <ProtectedRoute>
                <ApiDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/developer/docs"
            element={
              <ProtectedRoute>
                <ApiDocs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
        {showMobileNav && <MobileNav />}
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
